import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clock, Flag, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import RouteOverview from "@/components/RouteOverview";
import InteractiveRouteMap from "@/components/InteractiveRouteMap";
import MuseumExplorer from "@/components/MuseumExplorer";

export default function WalkDetail() {
  const { walkId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/walks/${walkId}`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setError("Dieser Weg ist nicht verfügbar."));
  }, [walkId]);

  useEffect(() => {
    if (!user || !data?.walk?.id) return;
    api.get(`/progress/${data.walk.id}`).then(({ data: p }) => setProgress(p)).catch(() => setProgress(null));
  }, [user, data?.walk?.id]);

  if (error) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">{error}</div>;
  if (!data) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;

  const { walk, stations } = data;
  const comingSoon = walk.status === "COMING_SOON";
  const done = (progress?.completed_stations || []).length;
  const completed = progress?.status === "COMPLETED";

  if (walk.experience_mode === "OPEN_COLLECTION") {
    return (
      <MuseumExplorer
        data={data}
        user={user}
        progress={progress}
        setProgress={setProgress}
      />
    );
  }

  const restart = async () => {
    const ok = window.confirm("SPUREN wirklich neu starten? Dein Fortschritt für diesen Weg wird zurückgesetzt. Bereits erhaltene Erfolge bleiben erhalten.");
    if (!ok) return;
    try {
      await api.post(`/progress/${walk.id}/reset`);
      navigate(`/wege/${walk.id}/los`);
    } catch {
      setError("Der Weg konnte nicht zurückgesetzt werden.");
    }
  };

  return (
    <div data-testid="walk-detail-page">
      <TopBar back="/wege" title={walk.title} />
      <div className="relative h-52 w-full bg-[var(--brand-soft)] grain">
        {walk.hero_image ? <img src={fileUrl(walk.hero_image)} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="mx-auto max-w-2xl px-5 py-7">
        <p className="eyebrow">{walk.category || "Weg"}</p>
        <h1 className="mt-1 text-4xl">{walk.title}</h1>
        {walk.subtitle ? <p className="mt-1 text-base text-[var(--ink-soft)]">{walk.subtitle}</p> : null}

        <div className="mt-5 grid gap-3 rounded-2xl border bg-white p-5 text-sm sm:grid-cols-2">
          {walk.distance_text ? (
            <p className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 opacity-50" /> {walk.distance_text}</p>
          ) : null}
          {walk.duration_text ? (
            <p className="inline-flex items-center gap-2"><Clock className="h-4 w-4 opacity-50" /> {walk.duration_text}</p>
          ) : null}
          {walk.start_location_text ? (
            <p className="inline-flex items-center gap-2"><Flag className="h-4 w-4 opacity-50" /> Start: {walk.start_location_text}</p>
          ) : null}
          {walk.end_location_text ? (
            <p className="inline-flex items-center gap-2"><Flag className="h-4 w-4 opacity-50" /> Ziel: {walk.end_location_text}</p>
          ) : null}
          {walk.target_group ? <p className="sm:col-span-2 text-[var(--ink-soft)]">{walk.target_group}</p> : null}
        </div>

        {walk.description ? <p className="mt-6 whitespace-pre-line text-base leading-relaxed">{walk.description}</p> : null}

        <div className="mt-7">
          <InteractiveRouteMap
            stations={stations}
            completedStationIds={progress?.completed_stations || []}
          />
        </div>

        <div className="mt-7">
          <RouteOverview
            stations={stations}
            segments={data.segments || []}
            completedStationIds={progress?.completed_stations || []}
          />
        </div>

        <div className="mt-8">
          {comingSoon ? (
            <p className="rounded-2xl border bg-white p-5 text-sm" data-testid="walk-coming-soon">
              Dieser Weg ist in Vorbereitung. Bald geht es los.
            </p>
          ) : user ? (
            completed ? (
              <button className="btn btn-primary w-full" onClick={restart} data-testid="walk-restart">
                WEG NOCH EINMAL GEHEN
              </button>
            ) : (
              <button className="btn btn-primary w-full" onClick={() => navigate(`/wege/${walk.id}/los`)} data-testid="walk-start">
                {done > 0 ? "WEG FORTSETZEN" : "WEG STARTEN"}
              </button>
            )
          ) : (
            <div className="rounded-2xl border bg-white p-5">
              <p className="eyebrow">Ohne Konto losgehen</p>
              <h2 className="mt-1 text-2xl">SPUREN funktioniert auch als Gast.</h2>
              <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">
                Als Gast wird dein Fortschritt nur auf diesem Gerät gespeichert. Wenn du SPUREN und Erfolge dauerhaft in deinem Profil sammeln möchtest, melde dich stattdessen vor dem Start an.
              </p>
              <button
                className="btn btn-primary mt-4 w-full"
                onClick={() => navigate(`/wege/${walk.id}/los?guest=1`)}
                data-testid="walk-guest-start"
              >
                ALS GAST STARTEN
              </button>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Link to="/login" className="btn btn-ghost !py-2.5 !text-xs" data-testid="walk-login">
                  ANMELDEN & SAMMELN
                </Link>
                <Link to="/registrieren" className="btn btn-ghost !py-2.5 !text-xs">
                  KONTO ANLEGEN
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
